import { getTemplateFirstChild } from '../../utils/dom'
import { FloatElement } from '.'
import { $el, $list } from 'src/scripts/utils/utils'
import { HeaderComponent } from 'src/scripts/sections/header'
import { BodyElement } from '../body-element/body-element'
import { HeaderFloatElementBtn } from './header-float-element-btn'
import { debounce } from 'src/scripts/utils/debounce'
import { isEscKey } from 'src/scripts/utils/key'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class HeaderFloatElement extends FloatElement {
  currentVisible: Element
  mounted: boolean

  constructor() {
    super()

    const templates = $list('template', this)

    const fragment = document.createDocumentFragment()
    templates.forEach(template => {
      const content = getTemplateFirstChild(template)
      content.setAttribute(
        'data-header-float-element-template',
        template.dataset.headerFloatElementTemplate,
      )
      fragment.appendChild(content)
    })

    this.element = fragment

    this.mounted = false

    this.initialPlacement = this.dataset.placement
  }

  mountComponent(): void {
    super.mountComponent()

    const header: HeaderComponent = $el('header-component')

    customElements.whenDefined('header-component').then(() => {
      header.on('headerButtonHover', this.handleButtonHover)
      header.on('mouseLeavedHeader', this.handleMouseLeave)
    })
  }

  unmountComponent(): void {
    super.unmountComponent()
  }

  handleMouseLeave = () => {
    if (!this.isOpen) {
      this.hide()
      this.toggleVideos(false)
    }
  }

  handleButtonHover = ({ floatElementId }) => {
    this.toggleElementVisible(floatElementId)

    this.show()
    this.toggleVideos(true)
  }

  toggleVideos = isToPlay => {
    const videos: HTMLVideoElement[] = $list('video', this)

    videos.forEach(video => {
      isToPlay ? video.play() : video.pause()
    })
  }

  toggleElementVisible = floatElementId => {
    this.currentVisible?.removeAttribute('visible')

    const parent = this.mounted ? this : this.element

    const visible = parent.querySelector(
      `[data-header-float-element-template=${floatElementId}]`,
    )

    visible.setAttribute('visible', '')
    this.currentVisible = visible
  }

  setVisible(isVisible: boolean) {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

    const styles = {
      zIndex: 2,
    }

    isVisible
      ? bodyElement.showOverlay(this.id, styles)
      : bodyElement.hideOverlay(this.id)
  }

  mount(): void {
    if (!this.mounted) {
      this.replaceChildren(this.element)
    }
    this.setUpdate()

    this.mounted = true
  }

  unmount(): void {
    this.removeUpdate()
  }

  handleDropdownKeyDown = (event: KeyboardEvent): void => {
    if (isEscKey(event)) {
      event.preventDefault()
    }
  }

  setBtnExpand = isVisible => {}

  handleBlockSelect = debounce(({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      if (this.header.navContainer.hasAttribute('hidden')) {
        return
      }

      const target = this.getTargetWhenBlockChange(blockId)

      if (!target) {
        this.header.handleMouseLeave()
        return
      }

      const floatElementId = target.getAttribute(
        'data-header-float-element-template',
      )

      const buttons = $list('[data-header-btn-with-backdrop]')

      const targetButton = buttons.find((btn: HeaderFloatElementBtn) => {
        if (btn.floatElementId) {
          return btn.floatElementId === floatElementId
        } else {
          const btnFloatElementId = btn.firstElementChild.getAttribute(
            'data-float-element-trigger',
          )

          return btnFloatElementId === floatElementId
        }
      })

      this.header.blockInEditorSelected = true
      this.header.handleButtonHover(targetButton)
      this.addListener(document, 'click', this.handleOutsideClick)
    }
  }, 50)

  handleBlockDeselect = debounce(({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      if (this.header.navContainer.hasAttribute('hidden')) {
        return
      }

      const target = this.getTargetWhenBlockChange(blockId)

      if (target === this.currentVisible) {
        this.header.blockInEditorSelected = false
        this.header.handleMouseLeave()
        this.removeListener(document, 'click', this.handleOutsideClick)
      }
    }
  }, 100)

  getTargetWhenBlockChange = blockId => {
    const parent = this.mounted ? this : this.element
    const target = $el(`[block-id="${blockId}"]`, parent)
    return target
  }

  handleOutsideClick = ({ clientY, clientX }) => {
    const target = document.elementFromPoint(clientX, clientY)
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    if (target === bodyElement) {
      this.header.blockInEditorSelected = false
      this.header.handleMouseLeave()
      this.removeListener(document, 'click', this.handleOutsideClick)
    }
  }

  get isOpen(): boolean {
    return this.parentElement.hasAttribute('with-opened-dropdown')
  }

  get header(): HeaderComponent {
    return $el('header-component')
  }
}
